import { clearAuthCookies, removeCookie } from "../utils/authCookies";

interface UserInfo {
  email: string;
  password: string;
}

export type PublicMaintenanceStatus = {
  _id?: string;
  enabled?: boolean;
  reason?: string;
  message?: string;
  started_at?: string;
  expected_end_at?: string;
};

interface LoginResponse {
  id: string;
  token: string;
  userType: string;
  licenseType: string;
  name: string;
  isProfileUpdated:boolean;
  error: string;
  maintenance?: PublicMaintenanceStatus;
}

interface RegisterResponse {
  success: boolean;
  message: string;
}

interface ActiveCartResponse {
  cart_id: string;
  cart_items: any[];
  shipping_address: any;
  tax_details: any;
  order_charges: any;
  order_tax_summary: any;
  total_amount: number;
  is_active: boolean;
  created_by: any;
  updated_by: any;
  created_at: string;
  supplier_id: string;
  updated_at: string;
}

export const fetchPublicMaintenanceStatus = async (): Promise<PublicMaintenanceStatus | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/maintenance/status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data && typeof data === "object") {
      return data as PublicMaintenanceStatus;
    }
    return null;
  } catch {
    return null;
  }
};

export const loginUser = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json', 
      },
      body: JSON.stringify({ email, password }),
    });

    // Check if the response status is not OK
    if (!response.ok) {
      // Attempt to read the error message from the response body
      const errorData = await response.json();
      // console.log('errorData:', errorData);
      // throw new Error(errorData.error || 'Login failed');
      return errorData;
    }

    // Parse successful response
    const data: LoginResponse = await response.json();
    return data;

  } catch (error) {
    // Log the error for debugging
    console.error('Error logging in:', error);
    throw error; // Re-throw to let the caller handle the error as well
  }
};

export const registerUser = async (userInfo: UserInfo): Promise<RegisterResponse> => {
  const response = await fetch('/api/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userInfo),
  });

  if (!response.ok) throw new Error('Registration failed');
  const data: RegisterResponse = await response.json();
  return data;
};

export const fetchActiveCart = async (token: string): Promise<ActiveCartResponse | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/active_cart`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch active cart');
      return null;
    }

    const data: ActiveCartResponse = await response.json();
    return data;

  } catch (error) {
    console.error('Error fetching active cart:', error);
    return null;
  }
};

export const logout = () => {
  clearAuthCookies();
  removeCookie("sessionToken");
  removeCookie("userType");
  removeCookie("userId");
  removeCookie("userName");
  removeCookie("licenseType");
  localStorage.removeItem('cart_id');
  localStorage.removeItem('supplierId');
};

/**
 * Call the logout API (POST /api/v1/logout) with the current JWT, then clear local storage.
 * Use this when the user clicks Logout so the server can invalidate the token.
 */
export const performLogout = async (token?: string | null) => {
  try {
    if (token) {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        console.warn('Logout API returned', response.status);
      }
    }
  } catch (err) {
    console.warn('Logout API request failed:', err);
  } finally {
    // Always clear auth data even when API logout fails.
    logout();
  }
};
